// グラフ.
import * as React from 'react';
import { Graph, Binding, getLocomoAxis2Max } from '../../utils/Graph';
import { MonthlyCountData } from '../../utils/MonthlyCountData';
import { JS_VOID, memoryReleaseForCanvas } from '../../utils/commonUtil';
import { MDate } from '../../utils/dateUtil';
import * as PropTypes from 'prop-types';
import * as TrainingConst from '../../constants/Training';

/**
 * グラフにデータを流し込む関数.
 *
 * @param {((HTMLCanvasElement | null)[])} elms 日常生活動作の欠番も含んだ配列.
 * @param {TrainingConst.TYPE} type
 * @param {*} monthlyCountData
 * @param {(boolean | undefined)} isLongTerm
 */
const insertDataCanvasGraphs = (elms: (HTMLCanvasElement | null)[], type: TrainingConst.TYPE,
                                monthlyCountData: any, isLongTerm: boolean | undefined) => {
  // tpl_locomo.jsのことをしている.
  const graph = new Graph();
  const width: number = 920;
  const height: number = 370;
  // 回数.
  const yAxis1Max: number = 30;
  // 角度.
  let yAxis2Max: number = 180;

  // ロコモかADLでY軸(角度)の最大値が違う.
  if (type === TrainingConst.TYPE.Locomo) {
    // ロコモは腕系のみ180度だがそれ以外は100なのに注意.
    yAxis2Max = 180;
  } else if (type === TrainingConst.TYPE.ADL) {
    yAxis2Max = 100;
  }

  let binding: Binding;

  if (isLongTerm) {
    // 長期変化は違う(LongTermの_lt).
    binding = {
      value: {
        // カウント数用のテーブル.
        history_monthly_lt: monthlyCountData.historyTableLongterm,
        // 角度用のテーブル.
        history_monthly_angle_lt: monthlyCountData.historyTableAngleLongterm,
      }
    } as Binding;
  } else {
    binding = {
      value: {
        // カウント数用のテーブル.
        history_monthly: monthlyCountData.historyTable,
        // 角度用のテーブル.
        history_monthly_angle: monthlyCountData.historyTableAngle,
      }
    } as Binding;
  }

  // 'myChartN'のidでグラフ生成する.
  let canvasCount = 0;
  if (type === TrainingConst.TYPE.Locomo) {
    canvasCount = TrainingConst.MAX_LOCOMO_CNT;
  } else if (type === TrainingConst.TYPE.ADL) {
    // ADLは欠番2つも含めた数(elms.lengthはJSが添字によって変化するので使わないこと).
    canvasCount = TrainingConst.MAX_ADL_CNT;
  }

  for (let i = 0; i < canvasCount; i++) {
    let menuNumber: number = i;
    const canvasElement: HTMLCanvasElement|null = elms[i];
    if (canvasElement === null) {
      continue;
    }

    if (type === TrainingConst.TYPE.Locomo) {
      // グラフのY軸角度の最大数を取得.
      yAxis2Max = getLocomoAxis2Max(i);
    } else if (type === TrainingConst.TYPE.ADL) {
      // ADLは欠番になってるのでここは飛ばす.
      // x 105: 更衣着脱衣(上半身).
      // x 106: 更衣着脱衣(靴下).
      if (TrainingConst.SKIP_ADL_IDX.indexOf(i) >= 0) {
        continue;
      }
    }

    if (isLongTerm) {
      // 長期変化は違う.
      graph.insertDataLongtermCanvasGraph(canvasElement, binding, menuNumber, width, height, yAxis1Max, yAxis2Max);
    } else {
      graph.insertDataCanvasGraph(canvasElement, binding, menuNumber, width, height, yAxis1Max, yAxis2Max);
    }
  }
};

// ローディング中のダミー・グラフ系 props.
interface DummyDailyHistoryProps extends React.Props<{}> {
  // 親からの引き渡し用.
  mDate: MDate;

  isLongTerm?: boolean;

  type: TrainingConst.TYPE;
}

// ローディング中のダミー・グラフ系.
export class DummyTrainingGraphs extends React.Component<DummyDailyHistoryProps, {}> {
  yearSelected: string;
  monthSelected: string;
  menuNames: string[];

  refCharts: (HTMLCanvasElement | null)[] = [];

  componentWillMount() {
    const mDate: MDate = this.props.mDate;
    this.yearSelected = mDate.year;
    this.monthSelected = mDate.month;

    if (this.props.type === TrainingConst.TYPE.Locomo) {
      this.menuNames = TrainingConst.SECTION_LOCOMO_NAMES;
    } else if (this.props.type === TrainingConst.TYPE.ADL) {
      this.menuNames = TrainingConst.SECTION_ADL_NAMES;
    }
  }

  componentDidMount() {
    const monthlyCountData = new MonthlyCountData(this.props.type);

    // グラフにデータを流し込む(ダミーなので空データ).
    insertDataCanvasGraphs(this.refCharts, this.props.type, monthlyCountData, this.props.isLongTerm);
  }

  componentWillUnmount() {
    // 縦横サイズを0にしてcanvasを開放する.
    memoryReleaseForCanvas(this.refCharts);
  }

  render() {
    return (
      <section id="sec04">
        <h2 className="ttlStyle01">1ヶ月の変化</h2>
        <div className="inner">
          <div className="calendarHeader">
            <div className="dateArea">
              <div className="date">{this.yearSelected}
                <span className="year">年</span>{this.monthSelected}
                <span>月</span>
              </div>
              <div className="btn left">
                <a />
              </div>
              <div className="btn right">
                <a />
              </div>
            </div>
              <div className="btnStyle05">
                <a>比較する</a>
              </div>
          </div>
          { this.menuNames.map((sectionName: string, index: number) => {
            const key = `section${index}`;

            // ADLは欠番になってるのでここは飛ばす.
            if (this.props.type === TrainingConst.TYPE.ADL) {
              // x 105: 更衣着脱衣(上半身).
              // x 106: 更衣着脱衣(靴下).
              if (TrainingConst.SKIP_ADL_IDX.indexOf(index) >= 0) {
                return (null);
              }
            }

            return <section key={key}>
                <h3>{sectionName}</h3>
                <canvas
                  ref={(item) => { this.refCharts[index] = item; }}
                  width="920"
                  height="300"
                />
              </section>;
            })
          }
        </div>
      </section>
    );
  }
}

// グラフ系props.
interface TrainingGraphsProps extends React.Props<{}> {
  // response内容.
  history: any;

  // 親からの引き渡し用.
  mDate: MDate;

  // 長期変化かどうか.
  isLongTerm?: boolean;

  // 種類.
  type: TrainingConst.TYPE;
}

/**
 * Canvasの棒グラフ（詳細画面, 長期）.
 *
 * @export
 * @class TrainingGraphs
 * @extends {React.Component<TrainingGraphsProps, {}>}
 */
export default class TrainingGraphs extends React.Component<TrainingGraphsProps, {}> {
  yearSelected: string;
  monthSelected: string;
  mDate: MDate;
  menuNames: string[];

  refCharts: (HTMLCanvasElement | null)[] = [];

  static get contextTypes() {
    // 親のLocomoコンポーネントと橋渡しするcontext.
    return {
      // 左右.
      callbackParent: PropTypes.func,
      // 比較.
      changeState: PropTypes.func,
    };
  }

  componentWillMount() {
    this.mDate = this.props.mDate;
    this.yearSelected = this.mDate.year;
    this.monthSelected = this.mDate.month;

    if (this.props.type === TrainingConst.TYPE.Locomo) {
      this.menuNames = TrainingConst.SECTION_LOCOMO_NAMES;
    } else if (this.props.type === TrainingConst.TYPE.ADL) {
      this.menuNames = TrainingConst.SECTION_ADL_NAMES;
    }
  }

  componentWillUnmount() {
    // 縦横サイズを0にしてcanvasを開放する.
    memoryReleaseForCanvas(this.refCharts);
  }

  componentDidMount() {
    const monthlyCountData = new MonthlyCountData(this.props.type);

    // 7行32列の配列にresponseデータを格納
    if (this.props.isLongTerm) {
      // 長期変化は違う.
      monthlyCountData.insertDataLongterm(this.props.history);
    } else  {
      monthlyCountData.insertData(this.props.history);
    }

    // グラフにデータを流し込む.
    insertDataCanvasGraphs(this.refCharts, this.props.type, monthlyCountData, this.props.isLongTerm);
  }

  // 左をクリックしたときの処理.
  minusMonthly = (yearSelected: string, monthSelected: string) => {
    // 先月.
    this.mDate.setPreMonth();

    // 親contextのメソッド.
    this.context.callbackParent(this.mDate.yearmonth);
  }

  // 右をクリックしたときの処理.
  plusMonthly = (yearSelected: string, monthSelected: string) => {
    // 次の月.
    this.mDate.setNextMonth();

    // 親contextのメソッド.
    this.context.callbackParent(this.mDate.yearmonth);
  }

  // 比較するをクリックしたときの処理.
  toLocomoCompare = () => {
    // 親contextのメソッド.
    this.context.changeState(TrainingConst.COMPARE_STATE);
  }

  render() {
    return (
      <section id="sec04">
        {(this.props.isLongTerm) ?
          (null)
          :
          <h2 className="ttlStyle01">1ヶ月の変化</h2>
        }
        <div className="inner">
          {(this.props.isLongTerm) ?
            (null)
            :
            <div className="calendarHeader">
              <div className="dateArea">
                <div className="date">{this.yearSelected}
                  <span className="year">年</span>{this.monthSelected}
                  <span>月</span>
                </div>
                <div className="btn left">
                  <a
                    href={JS_VOID} 
                    onClick={event => this.minusMonthly(this.yearSelected, this.monthSelected)}
                  />
                </div>
                <div className="btn right">
                  <a
                    href={JS_VOID} 
                    onClick={event => this.plusMonthly(this.yearSelected, this.monthSelected)}
                  />
                </div>
              </div>
              <div className="btnStyle05">
                <a onClick={event => this.toLocomoCompare()}>比較する</a>
              </div>
            </div>
          }
          { this.menuNames.map((sectionName: string, index: number) => {
            const key = `section${index}`;

            // ADLは欠番になってるのでここは飛ばす.
            if (this.props.type === TrainingConst.TYPE.ADL) {
              // x 105: 更衣着脱衣(上半身).
              // x 106: 更衣着脱衣(靴下).
              if (TrainingConst.SKIP_ADL_IDX.indexOf(index) >= 0) {
                return (null);
              }
            }

            return <section key={key}>
                <h3>{sectionName}</h3>
                <canvas
                  ref={(item) => { this.refCharts[index] = item; }}
                  width="920"
                  height="300"
                />
              </section>;
            })
          }
        </div>
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/common/TrainingGraphs.tsx